/**
 * @author omriha
 * 05/02/2020
 */
import React from "react";
import { connect, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import SMCPage from "../../../components/core/pageComponents/SMCPage";
import ButtonContainer from "../../../components/Simple/ButtonContainer";
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import {useUserEditor} from './UserEditorHelper';
import SMCButton from '../../../components/Simple/SMCButton';
import {userPermissionsSelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import ButtonsContainerStyles from '../ButtonsContainerStyles'
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import { setShowBackButton } from "../../../store/actions/view.actions";

/**
 * High level element to fetch, edit and save user, all logic implemented inside.
 * @param {string} id Id of user to fetch from the server to edit
 */
function UserEditor({ navigateTo, id, urlParams, userPermissions }) {
  const classes = ButtonsContainerStyles();
    const { t } = useTranslation();
  const { editorWidth, editorHeight } = useEditorDimensions();
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(setShowBackButton(true, t("SMC_USER_EDITOR_PAGENAME")));
    return () => {
      dispatch(setShowBackButton(false, ""));
    };
  }, [dispatch, t]);

  const modifyPermission = userPermissions ? userPermissions.modify : true;
  const deletePermission = userPermissions ? userPermissions.delete : true;
  // const createPermission = userPermissions ? userPermissions.create : true;

  let defaultPage = "general";
  const queryParams = queryString.parse(urlParams);
  if (queryParams && queryParams["page"]) {
    defaultPage = queryParams["page"];
  }

    const {
        userObject,
        isObjectDirty,
        editorPages,
        helpId,
        origUserObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setUserData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData,
        availableRoles,
        minimumPortalPasswordLength,
        maximumPortalPasswordLength,
    } = useUserEditor(id, defaultPage);

    // Data passed down to the editor pages
    const editorPageData = {
        userObject,
        origUserObject,
        setEditorPageId: onPageChange,
        setUserData,
        isPageDataLoaded,
        availableRoles,
        minimumPortalPasswordLength,
        maximumPortalPasswordLength,
    };

    return (
        <>
            <RouteLeavingGuard when={isObjectDirty} />
            <SMCPage loading={!isPageDataLoaded} helpId={helpId}>
                <div className="ObjectEditor" style={{ width: editorWidth }}>
                    <ButtonContainer className={classes.headerContainer}>
                        <LabelContainer value={id}/>
                        <div>
                            <div style={{display: 'flex'}}>
                                <ButtonContainer>
                                    <SMCButton labelClassName={classes.labelColor} icon="delete" variant="ghost" disabled={!isPageDataLoaded || !deletePermission} onClick={deleteButtonOnClick}>{t('SMP_DELETE_USER')}</SMCButton>
                                </ButtonContainer>
                            </div>
                        </div>
                    </ButtonContainer>
                    <SMCDisabledProvider forceDisable={!modifyPermission}>
                        <SMCEditor
                            editorPageData={editorPageData}
                            height={editorHeight}
                            width={editorWidth}
                            errorListByPage={errorMessagesByPageList}
                            chosenPageId={chosenPageId}
                            onPageChange={onPageChange}
                            isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                            isPageLoadFailed={isPageLoadFailed}
                            reloadData={reloadPageData}
                            pages={editorPages}
                        />
                    </SMCDisabledProvider>
                </div>
            </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded || !modifyPermission}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
  userPermissions: userPermissionsSelector(state),
  urlParams: state.router.location.search,
});

const mapDispatchToProps = {
  navigateTo: push,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserEditor);

